import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA, TextCTA } from "@/components/CTA";
import { Tick, Star } from "@/components/Annotation";
import { Tape, StickyNote } from "@/components/Notebook";
import { MethodologyTeaser } from "@/components/MethodologyTeaser";
import { ShareCaseCard } from "@/components/ShareCaseCard";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

const LOGOS = [
  { src: "/logos/amazon.png",  alt: "Amazon",                        w: "w-[150px] md:w-[190px]" },
  { src: "/logos/ert.png",     alt: "ΕΡΤ · Hellenic Broadcasting",   w: "w-[70px]  md:w-[90px]"  },
  { src: "/logos/avocado.png", alt: "Avocado Learning Experiences",  w: "w-[170px] md:w-[210px]" },
];

const FEEDBACK_PHRASES = [
  "Interaction and material discussed",
  "Great facilitator — very good work",
  "The facilitator was very engaging",
  "Interactive activities. Passion. Respect. Politeness.",
  "Great presentation and very helpful",
  "Engaging, to the point and resourceful under different circumstances",
  "Tips for grounding · the energy of the presenter · the actual examples",
  "Interactive — with real examples and scenarios",
  "The role play with the actor and the body exercises",
  "Interactive for most of its duration — a quality often missing from workshops",
  "The interactive exercises and the incorporation of physical practice",
];

// Existing Pantelis case-study URL (Insights CMS) — never invent a new one.
const PANTELIS_HREF = `${ROUTES.caseStudies}/pantelis-papachristou`;

const CASE_STATS = [
  { value: "17",    label: "Participants" },
  { value: "100%",  label: "Feedback participation" },
  { value: "5.0/5", label: "Facilitator rating" },
  { value: "4.8/5", label: "Workshop rating" },
];

const PARTICIPANT_QUOTES = [
  { q: "The facilitator was very engaging",                                                                        role: "Participant · Recruitment team" },
  { q: "Great presentation and very helpful",                                                                      role: "Participant · Recruitment team" },
  { q: "The practical part through the cases",                                                                     role: "Participant · Recruitment team" },
  { q: "Engaging, to the point and resourceful under different circumstances",                                     role: "Participant · Recruitment team" },
  { q: "Interactive activities. Passion. Respect. Politeness.",                                                     role: "Participant · Recruitment team" },
  { q: "The role play with the actor and the body exercises",                                                       role: "Participant · Recruitment team" },
  { q: "The workshop was interactive for most of its duration — a quality that is often missing from many workshops", role: "Participant · Recruitment team" },
  { q: "Tips for grounding · the energy of the presenter · the actual examples",                                    role: "Participant · Recruitment team" },
];

// Trust block reused from Corporate page pattern
const EplTrust = ({ t }) => (
  <section className="relative z-10 py-14 md:py-20 border-y border-ink/10 bg-paper-deep/40" data-testid="epl-trust">
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-8 md:mb-10">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-ink-soft text-center">
        {t("epl.trust_label")}
      </p>
    </Reveal>
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-wrap items-center justify-center gap-12 md:gap-20 mb-10 md:mb-14">
      {LOGOS.map((l) => (
        <img key={l.alt} src={l.src} alt={l.alt} draggable={false}
          className={`${l.w} h-auto opacity-90 hover:opacity-100 transition-opacity duration-500 select-none`} />
      ))}
    </div>
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-5 md:mb-6">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta text-center">
        {t("epl.trust_marquee_label")}
      </p>
    </Reveal>
    <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
      <div className="flex w-max animate-marquee items-center gap-10 md:gap-14 whitespace-nowrap">
        {[...FEEDBACK_PHRASES, ...FEEDBACK_PHRASES].map((phrase, i) => (
          <span key={i} className="inline-flex items-center gap-10 md:gap-14">
            <span className="font-serif italic text-lg md:text-2xl text-ink/55 hover:text-ink transition-colors duration-500">
              &ldquo;{phrase}&rdquo;
            </span>
            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta/60" />
          </span>
        ))}
      </div>
    </div>
  </section>
);

// Horizontally-scrolling quote cards (marquee of boxes)
const RollingQuoteCards = () => (
  <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)]" data-testid="epl-rolling-quotes">
    <div className="flex w-max animate-marquee items-stretch gap-6 md:gap-8">
      {[...PARTICIPANT_QUOTES, ...PARTICIPANT_QUOTES].map((c, i) => (
        <figure key={i} className="relative bg-paper border border-ink/12 p-7 md:p-8 shadow-[4px_8px_24px_rgba(27,27,24,0.06)] w-[320px] md:w-[380px] shrink-0">
          <span aria-hidden="true" className="absolute -top-4 -left-1 font-serif text-6xl text-terracotta/50 leading-none">&ldquo;</span>
          <blockquote className="font-serif italic text-lg md:text-xl text-ink leading-snug">{c.q}</blockquote>
          <figcaption className="mt-4 font-hand text-pencil text-base">— {c.role}</figcaption>
        </figure>
      ))}
    </div>
  </div>
);

export default function ExecutivePresenceForLeaders() {
  const { t } = useLang();
  const develop = t("epl.develop_items") || [];
  const situations = t("epl.situations_items") || [];
  const rehList = t("epl.rehearsal_list") || [];
  const outcomes = t("epl.outcomes_items") || [];
  const steps = t("epl.process_steps") || [];
  const whofor = t("epl.whofor_items") || [];
  const caseWorked = t("pce.case_worked") || [];
  const caseOutcome = t("pce.case_outcome") || [];

  return (
    <>
      <PageMeta
        titleKey="meta.epfl_title"
        descriptionKey="meta.epfl_description"
        path={ROUTES.executivePresenceForLeaders}
      />
      <PageHero
        testId="epl-hero"
        eyebrow={t("epl.hero_eyebrow")}
        headline={[t("epl.hero_h1_a"), t("epl.hero_h1_b")]}
        emphasis={t("epl.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("epl.hero_intro")}
        meta={<>{t("epl.hero_meta_line1")}<br />{t("epl.hero_meta_line2")}<br /><span className="text-mustard">{t("epl.hero_meta_line3")}</span></>}
      >
        <PrimaryCTA href={ROUTES.bookCorporate} testId="epl-primary-cta" location="hero" targetAssetId="booking_corporate">{t("epl.cta_book")}</PrimaryCTA>
        <OutlineCTA href={ROUTES.contact} testId="epl-brief-cta" location="hero" targetAssetId="contact_request">{t("epl.final_cta_brief")}</OutlineCTA>
      </PageHero>

      <EplTrust t={t} />

      {/* Belief */}
      <section className="relative z-10 py-24 md:py-32" data-testid="epl-belief">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">{t("epl.belief_eyebrow")}</p>
            <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.06] tracking-tight">
              {t("epl.belief_h1")}<br /><span className="italic">{t("epl.belief_h2")}</span>
            </blockquote>
            <div className="mt-8 space-y-5 font-body text-lg text-ink-soft max-w-3xl leading-relaxed">
              <p>{t("epl.belief_p1")}</p>
              <p>{t("epl.belief_p2")}</p>
              <p><strong className="text-ink font-normal">{t("epl.belief_p3")}</strong> {t("epl.belief_p4")}</p>
            </div>
            <p className="mt-8 font-hand text-terracotta text-2xl md:text-3xl">{t("epl.belief_note")}</p>
          </Reveal>
        </div>
      </section>

      {/* What we develop — 6 pillars */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10" data-testid="epl-develop">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.develop_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.develop_h1")}<br /><span className="italic">{t("epl.develop_h2")}</span>
            </h2>
          </Reveal>
          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {develop.map((p, i) => (
              <StaggerItem key={p.title}>
                <div className="relative bg-paper border border-ink/12 p-8 h-full shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
                  <span className="font-hand text-terracotta text-2xl leading-none">{String(i+1).padStart(2,"0")}</span>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-tight">{p.title}</h3>
                  <p className="mt-4 font-body text-ink-soft leading-relaxed">{p.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Situations */}
      <section className="relative z-10 py-24 md:py-32" data-testid="epl-situations">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.situations_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.situations_h1")}<br /><span className="italic">{t("epl.situations_h2")}</span>
            </h2>
            <p className="mt-8 font-hand text-terracotta text-2xl leading-snug max-w-md">{t("epl.situations_note")}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 bg-paper border border-ink/12 p-8 shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
              {situations.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <Tick className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-ink leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Attention shift */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10" data-testid="epl-attention">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.attention_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.attention_h1")}<br /><span className="italic">{t("epl.attention_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft italic leading-relaxed max-w-[52ch]">{t("epl.attention_q1")}</p>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[52ch]">{t("epl.attention_q2")}</p>
            <div className="mt-8 font-hand text-terracotta text-2xl md:text-3xl leading-snug">{t("epl.attention_flow")}</div>
            <p className="mt-8 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">{t("epl.attention_result")}</p>
            <p className="mt-8 font-serif italic text-2xl text-ink leading-snug max-w-[52ch]">{t("epl.attention_note")}</p>
          </Reveal>
        </div>
      </section>

      {/* Rehearsal principles */}
      <section className="relative z-10 py-24 md:py-32" data-testid="epl-rehearsal">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-6">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.rehearsal_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.rehearsal_h1")}<br /><span className="italic">{t("epl.rehearsal_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[58ch]">{t("epl.rehearsal_body")}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative bg-paper border border-ink/12 p-8 shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
              <Tape className="absolute -top-4 left-8" rotate={-4} width={100} />
              <ul className="space-y-3">
                {rehList.map((r) => (
                  <li key={r} className="flex items-start gap-3">
                    <span className="font-hand text-terracotta text-lg leading-none mt-1">→</span>
                    <span className="font-serif text-xl text-ink">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-6 font-serif italic text-2xl text-ink leading-snug">{t("epl.rehearsal_pull")}</p>
          </Reveal>
        </div>
      </section>

      {/* Organisation outcomes */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10" data-testid="epl-outcomes">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.outcomes_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.outcomes_h1")}<br /><span className="italic">{t("epl.outcomes_h2")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-4">
            {outcomes.map((o, i) => (
              <div key={o} className="flex items-start gap-5 border-t border-ink/15 pt-4">
                <span className="font-hand text-terracotta text-2xl leading-none shrink-0">{String(i+1).padStart(2,"0")}</span>
                <p className="font-body text-lg text-ink leading-relaxed">{o}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* 4-step process */}
      <section className="relative z-10 py-24 md:py-32" data-testid="epl-process">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.process_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.process_h1")}<br /><span className="italic">{t("epl.process_h2")}</span>
            </h2>
          </Reveal>
          <StaggerGroup className="lg:col-span-7 space-y-6">
            {steps.map((step, i) => (
              <StaggerItem key={step.label}>
                <div className="flex gap-6 items-start border-t border-ink/15 pt-6">
                  <span className="font-hand text-terracotta text-3xl leading-none shrink-0">{String(i+1).padStart(2,"0")}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-ink">{step.label}</h3>
                    <p className="mt-2 font-body text-ink-soft leading-relaxed">{step.body}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Workshop credibility — ratings + rolling quote cards */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10" data-testid="epl-workshop">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mb-14">
          <Reveal className="max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.workshop_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.workshop_h1")}<br /><span className="italic">{t("epl.workshop_h2")}</span>
            </h2>
          </Reveal>
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mb-14">
          <Reveal>
            <div className="relative bg-ink text-paper px-6 md:px-12 py-12 md:py-14">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-8 text-center">
                {t("epl.workshop_ratings_label")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
                {CASE_STATS.map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-serif text-5xl md:text-6xl lg:text-7xl text-mustard leading-none">{s.value}</p>
                    <p className="mt-3 font-body text-xs md:text-sm uppercase tracking-[0.2em] text-paper/80">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        {/* Rolling quote cards (as requested — cards slide sideways) */}
        <Reveal>
          <RollingQuoteCards />
        </Reveal>
      </section>

      {/* Featured case — Pantelis Papachristou */}
      <section
        className="relative z-10 py-24 md:py-32"
        data-testid="epl-pantelis-case"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("epl.pantelis_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.pantelis_h1")}
              <br />
              <span className="italic">{t("epl.pantelis_h2")}</span>
            </h2>
          </Reveal>

          <article className="grid lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-4">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mb-3">
                {t("pce.case_context")}
              </p>
              <p className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                {t("pce.case_who")}
              </p>
              <p className="font-hand text-lg text-pencil mt-1">{t("pce.case_role")}</p>

              <blockquote className="mt-8 border-l-2 border-terracotta pl-5 font-serif italic text-xl text-ink leading-snug">
                &ldquo;{t("pce.case_testimonial")}&rdquo;
              </blockquote>
              <p className="mt-4 font-hand text-terracotta text-base">
                — {t("pce.case_testimonial_tag")}
              </p>
              <div className="mt-5">
                <TextCTA href={PANTELIS_HREF} testId="epl-case-pantelis-link">
                  {t("epd.pantelis_link")}
                </TextCTA>
              </div>
            </div>

            <div className="lg:col-span-8 grid md:grid-cols-2 gap-8 md:gap-10">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("pce.case_challenge_label")}
                </p>
                <p className="font-body text-ink-soft leading-relaxed mb-8">
                  {t("pce.case_challenge")}
                </p>

                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("pce.case_worked_label")}
                </p>
                <ul className="space-y-2">
                  {caseWorked.map((w) => (
                    <li key={w} className="flex items-start gap-3">
                      <Tick className="w-4 h-4 mt-1 shrink-0" />
                      <span className="font-body text-ink leading-relaxed">{w}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="border-l-2 border-terracotta pl-5 mb-8">
                  <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                    {t("pce.case_outcome_label")}
                  </p>
                  <ul className="space-y-2">
                    {caseOutcome.map((o) => (
                      <li key={o} className="flex items-start gap-3">
                        <span className="font-hand text-terracotta text-lg leading-none mt-1">
                          →
                        </span>
                        <span className="font-body text-ink leading-relaxed">{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-paper-deep/50 border border-ink/12 p-6">
                  <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mb-3">
                    {t("pce.case_takeaway_label")}
                  </p>
                  <p className="font-serif italic text-lg md:text-xl text-ink leading-snug">
                    {t("pce.case_takeaway")}
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Who for */}
      <section className="relative z-10 py-24 md:py-32" data-testid="epl-whofor">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("epl.whofor_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epl.whofor_h1")}<br /><span className="italic">{t("epl.whofor_h2")}</span>
            </h2>
            <div className="mt-8">
              <StickyNote rotate={-2} color="#F2EBDA">{t("epl.whofor_note")}</StickyNote>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <ul className="space-y-3">
              {whofor.map((w) => (
                <li key={w} className="flex items-start gap-4 border-t border-ink/15 pt-4">
                  <Star className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-lg text-ink leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <ShareCaseCard programmeTitle="Executive Presence for Leaders" testId="epl-share" />
      <MethodologyTeaser testId="epl-methodology-teaser" />

      <section className="relative z-10 py-24 md:py-32 bg-ink text-paper" data-testid="epl-final-cta">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">{t("epl.final_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-paper">
              {t("epl.final_h1")}<br /><span className="italic">{t("epl.final_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-paper/90 max-w-2xl mx-auto leading-relaxed">{t("epl.final_body")}</p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center gap-5 flex-wrap">
            <PrimaryCTA href={ROUTES.bookCorporate} testId="epl-final-book-cta" location="final_cta" targetAssetId="booking_corporate">{t("epl.final_cta_book")}</PrimaryCTA>
            <OutlineCTA href={ROUTES.contact} testId="epl-final-brief-cta" location="final_cta" targetAssetId="contact_request"
              className="!border-paper/70 !text-paper hover:!border-mustard hover:!text-mustard">
              {t("epl.final_cta_brief")}
            </OutlineCTA>
          </Reveal>
        </div>
      </section>
    </>
  );
}
